import { Links, Meta, Scripts } from '@remix-run/react';
import { useEffect, useState } from 'react';
import type { ErrorResponse } from '@remix-run/react';

export const CustomErrorHandler = ({ caught }: { caught?: ErrorResponse }) => {
    type ErrorState = { broadcastPath?: string; searchUrl?: string | null };
    const [urls, setUrls] = useState<ErrorState>({});

    useEffect(() => {
        const broadcastPath = location.hostname + location.pathname;
        const adId = new URLSearchParams(window.location.search).get('finnkode');
        const searchUrl = adId ? '//' + location?.hostname + '/' + adId : null;
        setUrls({ broadcastPath, searchUrl });
    }, []);

    let errorHtml: JSX.Element | null = null;
    switch (caught?.status) {
        case 403:
            errorHtml = (
                <>
                    <h1>403</h1>
                    <p>
                        Oj da, det ser ikke ut som du har tilgang til denne siden. Hvis du forsøkte å se en forhåndvisning av boligannonsen,
                        ta kontakt med din megler.{' '}
                    </p>
                    <p>
                        <a href="https://www.finn.no">Gå til forsiden</a>
                    </p>
                    <img src="https://www.finn.no/assets/404.jpg" className="w-full max-w-full" alt="Katt som holder en FINN-annonse" />
                </>
            );
            break;
        case 404:
            errorHtml = (
                <>
                    <h1>404</h1>
                    {urls.searchUrl !== null && (
                        <p>
                            Annonsen finnes ikke her. Du kan prøve å <a href={urls.searchUrl}>søke på hele FINN</a>, eller{' '}
                            <a href="https://www.finn.no">gå til forsiden</a>.
                        </p>
                    )}
                    {urls.searchUrl === null && (
                        <>
                            <p>
                                På FINN finner du alt &hellip; bortsett fra denne siden. <a href="https://www.finn.no">Gå til forsiden</a>
                            </p>
                        </>
                    )}
                    <p></p>
                    <img src="https://www.finn.no/assets/404.jpg" className="w-full max-w-full" alt="Katt som holder en FINN-annonse" />
                </>
            );
            break;
        case 500:
            errorHtml = (
                <>
                    <h1>Beklager, nå er det rusk i maskineriet.</h1>
                    <p>
                        Prøv igjen om litt, eller få mer informasjon hos <a href="http://hjelpesenter.finn.no/">Hjelpesenteret</a>.
                    </p>
                    <img src="https://www.finn.no/assets/500.jpg" alt="Rufsete katt" className="w-full max-w-full" />
                </>
            );
            break;
        default:
            errorHtml = (
                <>
                    <h1>Beklager, nå er det rusk i maskineriet.</h1>
                    <p>
                        Prøv igjen om litt, eller få mer informasjon hos <a href="http://hjelpesenter.finn.no/">Hjelpesenteret</a>.
                    </p>
                    <img src="https://www.finn.no/assets/500.jpg" alt="Rufsete katt" className="w-full max-w-full" />
                </>
            );
    }

    return (
        <html lang="nb">
            <head>
                <meta charSet="utf-8" />
                <title>Usjda!</title>
                <meta name="viewport" content="width=device-width,initial-scale=1" />
                <meta name="robots" content="noindex" />
                <Meta />
                <link rel="stylesheet" href="https://assets.finn.no/pkg/@warp-ds/css/v1/tokens/finn-no.css" />
                <link rel="stylesheet" href="https://assets.finn.no/pkg/@warp-ds/fonts/v1/finn-no.css" />

                <link rel="stylesheet" href="https://assets.finn.no/pkg/@warp-ds/css/v1/resets.css" />
                <link rel="stylesheet" href="https://assets.finn.no/pkg/@warp-ds/css/v1/components.css" />
                <Links />
            </head>
            <body>
                <div className="page-container text-center my-64">
                    {urls.broadcastPath && <div></div>}
                    {errorHtml}
                </div>
                <Scripts />
            </body>
        </html>
    );
};
